import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Edit, Trash2, X, Printer } from 'lucide-react';
import { QuickCreateVendor, QuickCreateProduct } from '../../components/QuickCreateModals';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';
import { toast } from 'sonner';

const formatAddress = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return [
    a.address_line1,
    a.address_line2,
    [a.city, a.state].filter(Boolean).join(', '),
    [a.pincode, a.country].filter(Boolean).join(' ')
  ].map(s => (s || '').trim()).filter(Boolean).join('\n');
};

const blankItem = () => ({ sku: '', name: '', hsn_code: '', material_size: '', quantity: 0, unit: '', unit_price: 0, gst_rate: 18 });

// Standard terms prefilled on a new PO — editable per order before release.
const DEFAULT_PO_TERMS = `1. Material must strictly conform to the approved specification / artwork; any deviation requires prior written approval.
2. Each consignment must be accompanied by the supplier's Certificate of Analysis / Test Certificate and batch details.
3. Goods are accepted subject to inspection. Rejected material will be returned at the supplier's cost and a debit note raised.
4. Delivery must be completed on or before the delivery date stated above. Delay may attract cancellation of this order.
5. Prices are firm for the duration of this order. No escalation will be entertained.
6. Invoice must quote this PO number, HSN code and GSTIN; payment is released as per the payment terms stated above.
7. Packing must protect the material from moisture, dust and damage during transit.
8. This order is subject to jurisdiction of the courts at the buyer's registered office.`;

const emptyForm = {
  vendor_id: '', indent_id: '', delivery_date: '', payment_terms: 'Net 30',
  terms_conditions: DEFAULT_PO_TERMS,
  vendor_billing_address: '', vendor_shipping_address: '', notes: '',
  items: [blankItem()]
};

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [indents, setIndents] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusForm, setStatusForm] = useState({ status: '', notes: '' });
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, vendorsRes, indentsRes, inventoryRes, settingsRes] = await Promise.all([
        api.get('/purchase/orders'),
        api.get('/purchase/vendors'),
        api.get('/purchase/indents?status=approved'),
        api.get('/sales/inventory'),
        api.get('/sales/company-settings')
      ]);
      setOrders(ordersRes.data);
      setVendors(vendorsRes.data);
      setIndents(indentsRes.data);
      setInventory(inventoryRes.data);
      setCompanySettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onVendorChange = (vendorId) => {
    const v = vendors.find(x => x.id === vendorId);
    const addr = v ? formatAddress(v.address) : '';
    setFormData(prev => ({
      ...prev,
      vendor_id: vendorId,
      vendor_billing_address: prev.vendor_billing_address || addr,
      vendor_shipping_address: prev.vendor_shipping_address || addr
    }));
  };

  const calculateItemTotals = (item) => {
    const taxable = item.quantity * item.unit_price;
    const gstAmount = (taxable * item.gst_rate) / 100;
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;
    const total = taxable + gstAmount;
    return { ...item, taxable_amount: taxable, cgst, sgst, igst: 0, total_amount: total, received_quantity: 0 };
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, items: [blankItem()] });
    setShowModal(true);
  };

  const openEdit = (order) => {
    setEditingId(order.id);
    setFormData({
      vendor_id: order.vendor_id || '',
      indent_id: order.indent_id || '',
      delivery_date: (order.delivery_date || '').slice(0, 10),
      payment_terms: order.payment_terms || 'Net 30',
      terms_conditions: order.terms_conditions ?? '',
      vendor_billing_address: order.vendor_billing_address || '',
      vendor_shipping_address: order.vendor_shipping_address || '',
      notes: order.notes || '',
      items: (order.items && order.items.length > 0)
        ? order.items.map(it => ({ ...blankItem(), ...it }))
        : [blankItem()]
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemsWithTotals = formData.items.map(calculateItemTotals);
      const payload = { ...formData, items: itemsWithTotals };
      if (editingId) {
        await api.put(`/purchase/orders/${editingId}`, payload);
        toast.success('Purchase order updated');
      } else {
        const res = await api.post('/purchase/orders', payload);
        // Auto-submit for workflow approval
        try {
          await api.post('/workflow/approvals/request', {
            document_type: 'purchase_order', document_id: res.data.id,
            document_number: res.data.po_number, module: 'purchase', max_level: 3
          });
        } catch (wfErr) { /* Workflow optional - don't block PO creation */ }
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({ ...emptyForm, items: [blankItem()] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingId ? 'update' : 'create'} PO`);
    }
  };

  const printPO = (order) => {
    const co = companySettings || {};
    const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    const itemRows = (order.items || []).map((it, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td>${it.sku || ''}</td>
        <td>${it.name || ''}</td>
        <td>${it.material_size || '-'}</td>
        <td>${it.hsn_code || ''}</td>
        <td style="text-align:right">${it.quantity || 0} ${it.unit || ''}</td>
        <td style="text-align:right">${inr(it.unit_price)}</td>
        <td style="text-align:right">${it.gst_rate || 0}%</td>
        <td style="text-align:right">${inr(it.total_amount)}</td>
      </tr>`).join('');
    const delivery = order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB') : '';
    const w = window.open('', `po_print_${order.id}`, 'width=900,height=700');
    w.document.open();
    w.document.write(`<html><head><title>Purchase Order - ${order.po_number}</title>
      <style>
        @page{size:A4;margin:14mm}body{font-family:Arial,sans-serif;font-size:12px;color:#111;margin:0;padding:18px}
        .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #000;padding-bottom:10px;margin-bottom:10px}
        .company-info{text-align:right;font-size:10px;line-height:1.5}
        .doc-title{text-align:center;font-size:18px;font-weight:bold;margin:10px 0;text-decoration:underline}
        .meta{display:flex;justify-content:space-between;margin:8px 0}
        .addr-grid{display:flex;justify-content:space-between;gap:16px;margin:12px 0}
        .addr-box{width:48%;border:1px solid #bbb;padding:8px;font-size:11px}
        .addr-box h4{margin:0 0 4px;font-size:11px;text-transform:uppercase;color:#333}
        table.items{width:100%;border-collapse:collapse;margin-top:8px}
        table.items th,table.items td{border:1px solid #999;padding:6px 8px;font-size:11px}
        table.items th{background:#1a237e;color:#fff}
        .totals{margin-top:10px;text-align:right;font-size:12px}
        .terms{margin-top:14px;border:1px solid #bbb;padding:8px 10px;page-break-inside:avoid}
        .terms h4{margin:0 0 4px;font-size:11px;text-transform:uppercase}
        .terms div{white-space:pre-line;font-size:10.5px;line-height:1.5}
        .footer{display:flex;justify-content:space-between;margin-top:50px}
        .footer div{border-top:1px solid #000;padding-top:4px;width:180px;text-align:center;font-size:11px}
        @media print{button{display:none !important}}
      </style></head><body>
      <div class="header">
        <div><img src="https://hridya.dweb.co.in/hridayalogo.png" alt="logo" style="height:60px; width:auto;" /></div>
        <div class="company-info">${co.address || ''}<br/>${co.email || ''}<br/>${co.website || ''}</div>
      </div>
      <div class="doc-title">PURCHASE ORDER</div>
      <div class="meta">
        <div><strong>PO No:</strong> ${order.po_number}</div>
        <div><strong>Delivery Date:</strong> ${delivery}</div>
      </div>
      <div class="meta">
        <div><strong>Vendor:</strong> ${order.vendor_name || ''}${order.vendor_gstin ? ` &nbsp; <strong>GSTIN:</strong> ${order.vendor_gstin}` : ''}</div>
        <div><strong>Payment Terms:</strong> ${order.payment_terms || ''}</div>
      </div>
      ${order.indent_number ? `<div class="meta"><div><strong>Against Indent:</strong> ${order.indent_number}</div></div>` : ''}
      <div class="addr-grid">
        <div class="addr-box"><h4>Vendor Billing Address</h4><div style="white-space:pre-line">${order.vendor_billing_address || '-'}</div></div>
        <div class="addr-box"><h4>Vendor Shipping Address</h4><div style="white-space:pre-line">${order.vendor_shipping_address || '-'}</div></div>
      </div>
      <table class="items">
        <thead><tr><th>#</th><th>SKU</th><th>Material</th><th>Size</th><th>HSN</th><th>Qty</th><th>Rate</th><th>GST</th><th>Amount</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>
      <div class="totals">
        <div>Sub Total: ${inr(order.subtotal)}</div>
        <div>CGST: ${inr(order.cgst_total)} &nbsp; SGST: ${inr(order.sgst_total)}</div>
        <div style="font-size:14px;font-weight:bold;margin-top:4px">Grand Total: ${inr(order.total_amount)}</div>
      </div>
      ${order.notes ? `<p style="margin-top:12px"><strong>Notes:</strong> ${order.notes}</p>` : ''}
      ${order.terms_conditions ? `<div class="terms"><h4>Terms &amp; Conditions</h4><div>${order.terms_conditions}</div></div>` : ''}
      <div class="footer"><div>Prepared by</div><div>Authorised Signatory</div></div>
      <button onclick="window.print()" style="margin-top:24px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const openStatusModal = (order) => {
    setEditingOrder(order);
    setStatusForm({ status: order.status, notes: order.notes || '' });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/purchase/orders/${editingOrder.id}`, statusForm);
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO');
    }
  };

  const deleteOrder = async (id, poNumber) => {
    if (!window.confirm(`Are you sure you want to delete PO "${poNumber}"?`)) return;
    try {
      await api.delete(`/purchase/orders/${id}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PO');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, blankItem()]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'sku' && value) {
      const invItem = inventory.find(i => i.sku === value);
      if (invItem) {
        newItems[index].name = invItem.name;
        newItems[index].hsn_code = invItem.hsn_code || '';
        newItems[index].material_size = invItem.size || '';
        newItems[index].unit = invItem.unit;
        newItems[index].unit_price = invItem.unit_price || 0;
        newItems[index].gst_rate = invItem.gst_rate || 18;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const getStatusBadge = (status) => {
    const badges = { pending: 'badge-warning', approved: 'badge-success', partial: 'badge-info', received: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.vendor_gstin && order.vendor_gstin.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Purchase Orders</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/purchase-orders" filename="purchase_orders_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/purchase-orders" filename="purchase_orders_export.pdf" />
          <button onClick={openCreate} className="btn btn-primary" data-testid="create-po-btn">
            <Plus size={16} />
            Create PO
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search by PO #, vendor, or GSTIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="partial">Partial</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading purchase orders...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Vendor</th>
                  <th>Items</th>
                  <th className="text-right">Total Amount</th>
                  <th>Delivery Date</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} data-testid={`po-row-${order.po_number}`}>
                    <td className="font-mono">{order.po_number}</td>
                    <td>{order.vendor_name}</td>
                    <td>{order.items?.length || 0} items</td>
                    <td className="text-right tabular-nums">{order.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, style: 'currency', currency: 'INR' })}</td>
                    <td className="text-sm">{new Date(order.delivery_date).toLocaleDateString()}</td>
                    <td>
                      <span className={getStatusBadge(order.status)} data-testid={`status-${order.po_number}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(order)} className="btn btn-ghost btn-sm" title="Edit PO" data-testid={`edit-btn-${order.po_number}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => printPO(order)} className="btn btn-ghost btn-sm" title="Print / PDF" data-testid={`print-btn-${order.po_number}`}>
                          <Printer size={14} />
                        </button>
                        <button onClick={() => openStatusModal(order)} className="btn btn-ghost btn-sm" title="Update Status" data-testid={`status-btn-${order.po_number}`}>
                          Status
                        </button>
                        <button onClick={() => deleteOrder(order.id, order.po_number)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${order.po_number}`}>
                          <Trash2 size={14} color="#DC2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && editingOrder && (
        <div className="modal-overlay" data-testid="status-modal">
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Update PO {editingOrder.po_number}</h2>
            </div>
            <form onSubmit={handleStatusUpdate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="status-select">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="partial">Partial</option>
                    <option value="received">Received</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={statusForm.notes} onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })} data-testid="status-notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowStatusModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="update-status-btn">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create PO Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="po-modal">
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingId ? 'Edit Purchase Order' : 'Create Purchase Order'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">
                      Vendor *
                      <button type="button" onClick={() => setShowVendorModal(true)} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', marginLeft: '4px' }} data-testid="quick-vendor-btn"><Plus size={12} /></button>
                    </label>
                    <select className="form-select" value={formData.vendor_id} onChange={(e) => onVendorChange(e.target.value)} required data-testid="vendor-select">
                      <option value="">Select vendor...</option>
                      {vendors.map((vendor) => (<option key={vendor.id} value={vendor.id}>{vendor.name}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Link to Indent / Material Requisition</label>
                    <select className="form-select" value={formData.indent_id} onChange={(e) => setFormData({ ...formData, indent_id: e.target.value })} data-testid="indent-select">
                      <option value="">None</option>
                      {indents.map((indent) => (<option key={indent.id} value={indent.id}>{indent.indent_number} - {indent.department}</option>))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Delivery Date *</label>
                    <input type="date" className="form-input" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} required data-testid="delivery-date-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms *</label>
                    <select className="form-select" value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} data-testid="payment-terms-select">
                      <option value="100% Advance">100% Advance</option>
                      <option value="100% Against Delivery">100% Against Delivery</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Vendor Billing Address</label>
                    <textarea className="form-textarea" rows={3} value={formData.vendor_billing_address} onChange={(e) => setFormData({ ...formData, vendor_billing_address: e.target.value })} placeholder="Billing address (for invoicing)" data-testid="vendor-billing-address" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vendor Shipping Address</label>
                    <textarea className="form-textarea" rows={3} value={formData.vendor_shipping_address} onChange={(e) => setFormData({ ...formData, vendor_shipping_address: e.target.value })} placeholder="Shipping / delivery address" data-testid="vendor-shipping-address" />
                  </div>
                </div>

                <div className="form-group">
                  <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Items</label>
                    <button type="button" onClick={() => setShowProductModal(true)} className="btn btn-ghost btn-sm" data-testid="quick-product-btn"><Plus size={12} /> New Product</button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px' }}>SKU</th>
                          <th style={{ padding: '4px' }}>Name</th>
                          <th style={{ padding: '4px' }}>Size</th>
                          <th style={{ padding: '4px' }}>HSN</th>
                          <th style={{ padding: '4px' }}>Qty</th>
                          <th style={{ padding: '4px' }}>Unit</th>
                          <th style={{ padding: '4px' }}>Price</th>
                          <th style={{ padding: '4px' }}>GST%</th>
                          <th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index}>
                            <td style={{ padding: '4px' }}>
                              <select className="form-select" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} required style={{ minWidth: '100px', fontSize: '12px', padding: '4px' }}>
                                <option value="">Select...</option>
                                {inventory.map((inv) => (<option key={inv.sku} value={inv.sku}>{inv.sku}</option>))}
                              </select>
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.name} onChange={(e) => updateItem(index, 'name', e.target.value)} required style={{ minWidth: '120px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.material_size || ''} onChange={(e) => updateItem(index, 'material_size', e.target.value)} placeholder='e.g. 12µ x 740mm' style={{ width: '110px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.hsn_code} onChange={(e) => updateItem(index, 'hsn_code', e.target.value)} style={{ width: '80px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} required style={{ width: '60px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="text" className="form-input" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)} required style={{ width: '50px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} required style={{ width: '80px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              <input type="number" className="form-input" value={item.gst_rate} onChange={(e) => updateItem(index, 'gst_rate', parseFloat(e.target.value) || 0)} style={{ width: '50px', fontSize: '12px', padding: '4px' }} />
                            </td>
                            <td style={{ padding: '4px' }}>
                              {formData.items.length > 1 && (
                                <button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }} data-testid="add-item-btn">+ Add Item</button>
                </div>

                <div className="form-group">
                  <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                    <label className="form-label" style={{ margin: 0 }}>Terms &amp; Conditions</label>
                    <button type="button" onClick={() => setFormData({ ...formData, terms_conditions: DEFAULT_PO_TERMS })} className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} data-testid="load-default-terms">Load Standard Terms</button>
                  </div>
                  <textarea className="form-textarea" rows={8} value={formData.terms_conditions} onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })} placeholder="Terms &amp; conditions printed on this purchase order" data-testid="terms-conditions-input" />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditingId(null); }} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-po-btn">{editingId ? 'Update PO' : 'Create PO'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVendorModal && <QuickCreateVendor onCreated={(v) => { setVendors([...vendors, v]); setFormData({...formData, vendor_id: v.id}); }} onClose={() => setShowVendorModal(false)} />}
      {showProductModal && <QuickCreateProduct onCreated={(p) => { setInventory([...inventory, p]); }} onClose={() => setShowProductModal(false)} />}
    </div>
  );
};

export default PurchaseOrders;
